import React, { useEffect, useReducer } from 'react';
import { Navigate } from 'react-router-dom';
import authService from '../services/AuthService';

/**
 * ProtectedRoute component
 * Wraps routes that require authentication
 * Redirects to login if user is not authenticated
 * 
 * Requirements: 5.3 - Route protection for authenticated users
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 */
function ProtectedRoute({ children }) {
  const [, refreshAuth] = useReducer(value => value + 1, 0);
  useEffect(() => {
    const checkSession = () => {
      refreshAuth();
      if (!document.hidden && authService.isLoggedIn()) void authService.validateSession?.();
    };
    window.addEventListener('authStateChanged', refreshAuth);
    window.addEventListener('storage', checkSession);
    window.addEventListener('focus', checkSession);
    document.addEventListener('visibilitychange', checkSession);
    const expiryCheck = setInterval(refreshAuth, 60000);
    checkSession();
    return () => {
      window.removeEventListener('authStateChanged', refreshAuth);
      window.removeEventListener('storage', checkSession);
      window.removeEventListener('focus', checkSession);
      document.removeEventListener('visibilitychange', checkSession);
      clearInterval(expiryCheck);
    };
  }, []);
  const isAuthenticated = authService.isLoggedIn();

  if (!isAuthenticated) {
    // Redirect to login page if not authenticated
    return <Navigate to="/login" replace />;
  }

  // Render children if authenticated
  return children;
}

export default ProtectedRoute;
